import * as i0 from '@angular/core';
import { Directive, Inject, Input } from '@angular/core';
import { fromEvent } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { CoerceBoolean } from '@slidebean/hydra/coercion';
import { didChange } from '@slidebean/hydra/did-change';
import * as i3 from '@slidebean/hydra/popper';
import { PopperFactory, PopperPrimitive } from '@slidebean/hydra/popper';
import * as i1 from '@slidebean/hydra/dynamic';
import { ContentRenderer, DynamicComponentFactory } from '@slidebean/hydra/dynamic';
import { DOCUMENT } from '@angular/common';
import * as i2 from '@slidebean/hydra/animator';

var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const POPOVER_PROVIDERS = [
    { provide: PopperFactory, useClass: PopperFactory },
    { provide: ContentRenderer, useClass: ContentRenderer },
    { provide: DynamicComponentFactory, useClass: DynamicComponentFactory },
];
const DEFAULT_PLACEMENT = 'auto';
class PopoverPrimitive extends PopperPrimitive {
    constructor(document, componentFactory, animator, popperFactory, element, zone) {
        super(popperFactory, element, zone);
        this.document = document;
        this.componentFactory = componentFactory;
        this.animator = animator;
        this.trigger = 'manual';
        this.placement = DEFAULT_PLACEMENT;
        this.hoist = false;
        this.disabled = false;
    }
    ngAfterViewInit() {
        super.ngAfterViewInit();
        this.zone.runOutsideAngular(() => {
            const target = this.getTarget();
            fromEvent(target, 'click')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => this.zone.run(() => this.onClick()));
        });
    }
    ngOnChanges(changes) {
        if (!this.popper)
            return;
        if (didChange(changes['disabled']) && this.disabled) {
            this.hide();
        }
        if (didChange(changes['content'])) {
            if (!!this.ref) {
                this.ref.destroy();
                this.ref = void 0;
            }
            this.popper.setContent(this.getContent());
            return;
        }
        if (didChange(changes['placement']) && didChange(changes['hoist'])) {
            this.popper.setOptions(this.getOptions());
        }
    }
    ngOnDestroy() {
        if (!this.ref)
            return;
        this.ref.destroy();
        this.ref = void 0;
    }
    async show() {
        if (!this.popper || this.disabled)
            return;
        if (!!this.ref)
            await this.animator.stop(this.ref.element);
        await super.show();
        if (!this.ref)
            return;
        this.zone.runOutsideAngular(() => {
            const target = this.getTarget();
            fromEvent(this.document, 'click')
                .pipe(filter((event) => {
                if (!this.popper || !this.ref || this.hasTrigger('manual')) {
                    return false;
                }
                const clicked = event?.target;
                if (this.ref.element?.contains(clicked) &&
                    !this.getKeepOpenOnContentClick() &&
                    this.popper.isOpen) {
                    return true;
                }
                return (!this.ref.element?.contains(clicked) &&
                    !target.contains(clicked) &&
                    this.popper.isOpen);
            }), takeUntil(this.destroy$))
                .subscribe(() => this.zone.run(() => this.hide()));
            fromEvent(this.document, 'keydown')
                .pipe(filter((event) => {
                if (!this.popper ||
                    !this.ref ||
                    this.hasTrigger('manual') ||
                    event.key !== 'Escape') {
                    return false;
                }
                const clicked = event?.target;
                return (!this.ref.element?.contains(clicked) &&
                    !target.contains(clicked) &&
                    this.popper.isOpen);
            }), takeUntil(this.destroy$))
                .subscribe(e => this.zone.run(() => {
                e.stopImmediatePropagation();
                this.hide();
            }));
        });
        const animation = this.getShowAnimation();
        if (!animation || !this.ref)
            return;
        await this.animator.animate(this.ref.element, animation);
    }
    async hide() {
        if (!this.popper)
            return;
        if (!!this.ref) {
            await this.animator.stop(this.ref.element);
            const animation = this.getHideAnimation();
            if (!!animation && !!this.ref) {
                await this.animator.animate(this.ref.element, animation);
            }
        }
        await super.hide();
    }
    getContent() {
        if (!!this.ref)
            return this.ref.element;
        this.ref = this.createPopoverComponent(this.content);
        return this.ref.element;
    }
    getOptions() {
        return {
            placement: this.placement ?? DEFAULT_PLACEMENT,
            strategy: this.hoist ? 'fixed' : 'absolute',
            modifiers: [
                {
                    name: 'arrow',
                    options: { element: this.getArrowSelector() },
                },
                {
                    name: 'arrow-position',
                    enabled: true,
                    phase: 'write',
                    fn({ state }) {
                        const { arrow } = state.elements;
                        if (!arrow)
                            return;
                        arrow.setAttribute('data-popper-placement', state.placement);
                    },
                    requires: ['arrow'],
                },
            ],
        };
    }
    // TODO: Improve the way to provide this kind of options:
    // Arrow selector, animations and delay
    getArrowSelector() {
        return '[data-popper-arrow]';
    }
    getShowAnimation() {
        return void 0;
    }
    getHideAnimation() {
        return void 0;
    }
    getKeepOpenOnContentClick() {
        return true;
    }
    hasTrigger(trigger) {
        const triggers = this.trigger?.split?.(' ') ?? [];
        return triggers.includes(trigger);
    }
    onClick() {
        if (!this.popper || !this.hasTrigger('click'))
            return;
        if (this.popper.isOpen)
            this.hide();
        else
            this.show();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopoverPrimitive, deps: [{ token: DOCUMENT }, { token: i1.DynamicComponentFactory }, { token: i2.Animator }, { token: i3.PopperFactory }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: PopoverPrimitive, inputs: { content: "content", trigger: "trigger", placement: "placement", hoist: "hoist", disabled: "disabled" }, usesInheritance: true, usesOnChanges: true, ngImport: i0 }); }
}
__decorate([
    CoerceBoolean()
], PopoverPrimitive.prototype, "hoist", void 0);
__decorate([
    CoerceBoolean()
], PopoverPrimitive.prototype, "disabled", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopoverPrimitive, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i1.DynamicComponentFactory }, { type: i2.Animator }, { type: i3.PopperFactory }, { type: i0.ElementRef }, { type: i0.NgZone }], propDecorators: { content: [{
                type: Input
            }], trigger: [{
                type: Input
            }], placement: [{
                type: Input
            }], hoist: [{
                type: Input
            }], disabled: [{
                type: Input
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { POPOVER_PROVIDERS, PopoverPrimitive };
